import React, { useState, useEffect, useCallback, useContext } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress,
  TextField, InputAdornment, Tooltip, IconButton, Snackbar, Alert,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CONTEXTS } from 'figshelf-modules';

const sourceColor = (s) => ({
  payment: 'primary', admin: 'default', promo: 'secondary', discount_code: 'warning',
}[s] ?? 'default');

const statusColor = (s) => ({ active: 'success', inactive: 'default', cancelled: 'error' }[s] ?? 'default');

const PremiumSubscribers = () => {
  const api = useContext(CONTEXTS.APIContext);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchSubscribers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/premium/admin/subscribers');
      const raw = res?.data ?? res ?? [];
      setSubscribers(Array.isArray(raw) ? raw : []);
    } catch { notify('Failed to load subscribers', 'error'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const handleToggle = async (sub) => {
    try {
      const userId = sub.userId;
      if (sub.status === 'active') {
        await api.post(`/premium/admin/deactivate/${userId}`);
        notify(`Deactivated premium for user`);
      } else {
        await api.post(`/premium/admin/activate/${userId}`);
        notify(`Activated premium for user`);
      }
      fetchSubscribers();
    } catch (err) {
      notify(err?.response?.data?.message || 'Action failed', 'error');
    }
  };

  const filtered = subscribers.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.user?.username?.toLowerCase().includes(q) ||
      s.user?.email?.toLowerCase().includes(q) ||
      s.userId?.toLowerCase().includes(q)
    );
  });

  const activeCount = subscribers.filter((s) => s.status === 'active').length;

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>Premium Subscribers</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              View and manage all premium subscriptions
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchSubscribers}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>

        {/* Summary */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Subscriptions', value: subscribers.length, icon: <WorkspacePremiumIcon color="primary" /> },
            { label: 'Active', value: activeCount, icon: <WorkspacePremiumIcon color="warning" /> },
            { label: 'Inactive / Cancelled', value: subscribers.length - activeCount, icon: <BlockIcon color="disabled" /> },
          ].map((s) => (
            <Card key={s.label} sx={{ flex: '1 1 150px', borderRadius: 3 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {s.icon}
                <Box>
                  <Typography variant="h6" fontWeight={700}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search by username, email or user ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ mb: 2 }}
          size="small"
        />

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Expires</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            {search ? 'No results match your search.' : 'No subscriptions found.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {filtered.map((sub) => (
                      <TableRow key={sub.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={sub.user?.profile?.currentAvatar} sx={{ width: 32, height: 32 }}>
                              {sub.user?.username?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>@{sub.user?.username ?? '—'}</Typography>
                              <Typography variant="caption" color="text.secondary">{sub.user?.email ?? sub.userId}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={sub.status} color={statusColor(sub.status)} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip label={sub.source?.replace('_', ' ')} color={sourceColor(sub.source)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sub.startedAt ? new Date(sub.startedAt).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={sub.expiresAt && new Date(sub.expiresAt) < new Date() ? 'error' : 'text.primary'}>
                            {sub.expiresAt ? new Date(sub.expiresAt).toLocaleDateString() : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={sub.status === 'active' ? 'Deactivate Premium' : 'Activate Premium'}>
                            <IconButton
                              size="small"
                              color={sub.status === 'active' ? 'error' : 'success'}
                              onClick={() => handleToggle(sub)}
                            >
                              {sub.status === 'active' ? <BlockIcon fontSize="small" /> : <WorkspacePremiumIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default PremiumSubscribers;
